/**
 * The dashboard's storyboard sign-off (F-133): the two decisions a project
 * manager makes on a board sitting in Request Review, asked the way
 * AssignStatusDialog asks its question — a modal that names the board and the
 * move, collects what the server needs, and reports the outcome.
 *
 *   approve   — an optional note, and the board leaves for Shooting
 *   send_back — a required note (the server refuses an empty rejection)
 *
 * This exists so a project manager working the kanban does not have to open
 * every board to sign it off. WHO the shoot lands on is not asked here: it is
 * the workspace's shooting-owners preset (/admin), because the answer is the
 * same people every time and asking it per approval turns a hand-off into a
 * form.
 */
import type { BoardSummary, StoryboardReviewResponse } from "@kola/shared";
import { useState } from "react";

import { approveStoryboard, rejectStoryboard } from "../../core/api.js";
import { Modal } from "../../ui/Modal.js";
import { gateErrorMessage } from "../bgm/gate-errors.js";

export type StoryboardGateAction = "approve" | "send_back";

const TITLES: Record<StoryboardGateAction, string> = {
  approve: "Approve storyboard",
  send_back: "Send storyboard back",
};

const SUBMIT_LABELS: Record<StoryboardGateAction, string> = {
  approve: "Approve and start shooting",
  send_back: "Send back",
};

export function StoryboardGateDialog({
  board,
  action,
  onClose,
  onDone,
}: {
  board: BoardSummary;
  action: StoryboardGateAction;
  onClose: () => void;
  /** Called after a successful gate call, with the server's response. */
  onDone: (response: StoryboardReviewResponse) => void;
}) {
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const needsNote = action === "send_back";
  const ready = !needsNote || note.trim() !== "";

  const submit = async () => {
    if (!ready || saving) return;
    setSaving(true);
    setError(null);
    const trimmed = note.trim();
    try {
      const response =
        action === "approve"
          ? await approveStoryboard(board.id, trimmed ? { note: trimmed } : {})
          : await rejectStoryboard(board.id, trimmed);
      onDone(response);
    } catch (err) {
      setSaving(false);
      setError(gateErrorMessage(err));
    }
  };

  return (
    <Modal title={TITLES[action]} onClose={saving ? () => undefined : onClose}>
      <div className="assign-dialog">
        <p className="assign-dialog-lead">
          {action === "approve" ? (
            <>
              Sign off the storyboard for <strong>{board.name}</strong>? The board moves to
              Shooting and its task goes to the shooting owners.
            </>
          ) : (
            <>
              Send the storyboard for <strong>{board.name}</strong> back to the artists. Say
              what needs to change — the note is required, and it lands on their task.
            </>
          )}
        </p>

        <label className="bgm-field">
          <span className="bgm-field-label">Note</span>
          <input
            type="text"
            className="bgm-input"
            autoFocus
            placeholder={needsNote ? "What needs to change" : "Optional"}
            value={note}
            disabled={saving}
            onChange={(event) => setNote(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") void submit();
            }}
          />
        </label>

        {error !== null && <div className="error-note">{error}</div>}

        <div className="modal-actions">
          <button type="button" className="btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void submit()}
            disabled={!ready || saving}
          >
            {saving ? "Working…" : SUBMIT_LABELS[action]}
          </button>
        </div>
      </div>
    </Modal>
  );
}
